import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Close, TrendingDown, TrendingFlat, TrendingUp } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { metricLabel } from 'components/play/constants';
import { formatDuration } from 'components/play/workoutEstimate';

const fmtDate = (iso) => {
  try {
    return format(parseISO(iso), 'd MMM yyyy', { locale: es });
  } catch {
    return '';
  }
};

const valueLabel = (result) =>
  result?.metric === 'TIME_SECONDS' ? formatDuration(result.value) : String(result?.value ?? '');

// Constante de módulo: `results` es dependencia del useMemo que ordena el historial (y ese
// resultado alimenta un useEffect), así que un `[]` en la firma se recrearía en cada render.
const DEFAULT_RESULTS = [];

/**
 * OTRIX Play — comparador de dos marcas del MISMO ejercicio: elige dos registros del
 * historial y ve la diferencia absoluta y porcentual con barras proporcionales. Modelado
 * sobre el comparador del entrenador (OTRIXVERSE) pero alimentado por myPlayResults.
 */
function PlayCompareModal({ open, onClose, exerciseName = '', results = DEFAULT_RESULTS }) {
  const { t } = useTranslation(['play']);

  const ordered = useMemo(
    () => [...(results || [])].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)),
    [results]
  );

  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');

  useEffect(() => {
    if (open && ordered.length >= 2) {
      setFromId(String(ordered[0].id));
      setToId(String(ordered[ordered.length - 1].id));
    }
  }, [open, ordered]);

  const from = ordered.find((r) => String(r.id) === fromId) || null;
  const to = ordered.find((r) => String(r.id) === toId) || null;

  const delta = from && to ? Number(to.value) - Number(from.value) : 0;
  // En tiempo, MÁS segundos es mejor marca (plancha/isométricos): el signo se lee igual.
  const percent = from && Number(from.value) > 0 ? Math.round((delta / Number(from.value)) * 100) : 0;
  const improved = delta > 0;
  const flat = delta === 0;

  let TrendIcon = TrendingFlat;
  if (!flat) TrendIcon = improved ? TrendingUp : TrendingDown;
  let trendColor = 'text.secondary';
  if (!flat) trendColor = improved ? 'trend.up' : 'trend.down';

  const max = Math.max(1, Number(from?.value) || 0, Number(to?.value) || 0);

  const renderPick = (label, value, onChange, excludeId) => (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{ flex: 1, minWidth: 0 }}>
      {ordered
        .filter((r) => String(r.id) !== excludeId)
        .map((r) => (
          <MenuItem key={r.id} value={String(r.id)}>
            {fmtDate(r.created_at)} · {valueLabel(r)}
          </MenuItem>
        ))}
    </TextField>
  );

  const renderBar = (result, highlight) => (
    <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
        {fmtDate(result?.created_at)}
      </Typography>
      <Typography variant="h5" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {valueLabel(result)}
      </Typography>
      <Box sx={{ height: 8, borderRadius: 999, backgroundColor: 'surface.overlay', overflow: 'hidden' }}>
        <Box
          sx={{
            height: '100%',
            width: '100%',
            borderRadius: 999,
            backgroundColor: highlight ? 'gold.main' : 'hairline.strong',
            transform: `scaleX(${Math.max(0.04, (Number(result?.value) || 0) / max)})`,
            transformOrigin: 'left',
            transition: 'transform 500ms cubic-bezier(0.32, 0.72, 0, 1)'
          }}
        />
      </Box>
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: 'surface.content',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: 'hairline.main'
        }
      }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'hairline.main' }}>
        <Stack spacing={0} sx={{ minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800} noWrap>
            {t('compare.title', { ns: 'play', defaultValue: 'Comparar marcas' })}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
            {exerciseName}
          </Typography>
        </Stack>
        <IconButton size="small" onClick={onClose} aria-label={t('recovery.close', { ns: 'play' })} sx={{ color: 'text.secondary' }}>
          <Close fontSize="small" />
        </IconButton>
      </Stack>

      <DialogContent>
        {ordered.length < 2 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}>
            {t('compare.needTwo', { ns: 'play', defaultValue: 'Necesitas al menos dos marcas de este ejercicio para comparar.' })}
          </Typography>
        ) : (
          <Stack spacing={2.5} sx={{ pt: 0.5 }}>
            <Stack direction="row" spacing={1.5}>
              {renderPick(t('compare.from', { ns: 'play', defaultValue: 'Antes' }), fromId, setFromId, toId)}
              {renderPick(t('compare.to', { ns: 'play', defaultValue: 'Después' }), toId, setToId, fromId)}
            </Stack>

            <Stack direction="row" spacing={2} alignItems="flex-end">
              {renderBar(from, false)}
              {renderBar(to, true)}
            </Stack>

            {/* Delta */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="center"
              sx={{
                py: 1.25,
                borderRadius: '12px',
                border: '1px solid',
                borderColor: 'hairline.main',
                backgroundColor: 'surface.overlay'
              }}>
              <TrendIcon sx={{ color: trendColor }} />
              <Typography variant="h6" fontWeight={800} sx={{ color: trendColor, fontVariantNumeric: 'tabular-nums' }}>
                {delta > 0 ? '+' : ''}
                {to?.metric === 'TIME_SECONDS' ? formatDuration(Math.abs(delta)) : Math.abs(delta)}
                {` (${percent > 0 ? '+' : ''}${percent}%)`}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {to ? metricLabel(t, to.metric) : ''}
              </Typography>
            </Stack>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

PlayCompareModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  exerciseName: PropTypes.string,
  /** SelfReportedResult[] del MISMO ejercicio. */
  results: PropTypes.arrayOf(PropTypes.object)
};

export default PlayCompareModal;
